import axios from "axios"
import * as React from "react"
import { useState } from "react"
import type { SubmitHandler } from "react-hook-form"
import { useForm } from "react-hook-form"

import clsxm from "@/lib/clsxm"

import Button from "@/components/common/Buttons/Button"
import Heading from "@/components/common/Heading/Heading"
import Icon from "@/components/common/Icon/Icon"
import UnstyledLink from "@/components/common/Links/UnstyledLink"
import TextField from "@/components/common/Textfield/TextField"

import { CheckCircleIcon } from "@/constants/icons"
import { EmailValidation, ValidationMessage } from "@/constants/validation"

import NewsletterDivider from "../../../public/svg/homepage/newsletter-divider.svg"

type NewsletterData = {
  email: string
}

export default function NewsletterSubscription() {
  const [loading, setLoading] = useState(false)
  const [subscribed, setSubscribed] = useState(false)
  const [apiError, setApiError] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewsletterData>()

  const handleNewsletterSignUp: SubmitHandler<NewsletterData> = async ({
    email,
  }: NewsletterData) => {
    setLoading(true)
    try {
      const response = await axios
        .post(
          `/api/subscribe-newsletter`,
          { email: email },
          { headers: { "Content-Type": `application/json` } }
        )
        .then(() => {
          setLoading(false)
          setSubscribed(true)
        })
      console.log(response)
    } catch (error) {
      setLoading(false)
      setApiError(true)
      console.log(error)
    }
  }

  return (
    <div
      className={clsxm(`relative z-10 flex justify-center bg-primary-50 pb-10`)}
    >
      <NewsletterDivider className={`absolute left-0 -top-1 h-auto  w-full`} />
      <div
        className={clsxm(
          `z-10 mx-4 flex w-full max-w-7xl items-center justify-center rounded-2xl bg-quilotoagreen p-8 `,
          `sm:p-10 sm:py-16`
        )}
      >
        <div
          className={clsxm(
            ` w-full max-w-5xl grid-cols-12 items-center gap-2`,
            `sm:grid sm:grid-cols-12`
          )}
        >
          <div
            className={clsxm(`col-span-5 mb-8 flex-1 text-white`, `sm:mb-0`)}
          >
            <Heading
              className={`mb-6 text-3xl font-bold tracking-tight`}
              headingLevel={`h4`}
            >
              Subscribe to our newsletter
            </Heading>
            <p>
              Keep up to date with new services, new locations and other
              exciting things.
            </p>
          </div>
          <div
            className={clsxm(
              `col-span-6 col-start-7`,
              `lg:col-span-5 lg:col-start-8`
            )}
          >
            {subscribed ? (
              <div
                className={`flex items-center rounded-md bg-mint py-2 px-4 text-green-800`}
              >
                <Icon className={`mr-3`} icon={CheckCircleIcon} />
                <span className={`font-semibold`}>Thanks for subscribing!</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit(handleNewsletterSignUp)}>
                <div
                  className={`relative flex items-center overflow-hidden rounded-full bg-white px-2 py-2`}
                >
                  <TextField
                    {...register(`email`, {
                      required: ValidationMessage.FIELD_REQUIRED,
                      pattern: {
                        value:
                          // eslint-disable-next-line no-useless-escape
                          EmailValidation.PATTERN,
                        message: EmailValidation.MESSAGE,
                      },
                    })}
                    wrapperClassName={`flex-1 w-full`}
                    className={`py-1`}
                    label={`Email`}
                    id={`newsletter-subscription`}
                    type={`text`}
                    variant={`minimal`}
                    placeholder={`Email`}
                  />
                  <Button
                    isLoading={loading}
                    type={`submit`}
                    className={`leading-none`}
                    isRounded
                  >
                    Subscribe
                  </Button>
                </div>
                {errors.email?.message && (
                  <span
                    className={`mt-2 block rounded-md bg-red-100 px-4 py-2 text-sm text-red-500`}
                  >
                    {errors.email?.message}
                  </span>
                )}
              </form>
            )}
            {apiError && (
              <div
                className={`mt-2 block rounded-md bg-red-100 px-4 py-2 text-sm text-red-500`}
              >
                Something went wrong. Please try again later. Contact us at{` `}
                <UnstyledLink
                  className={`font-semibold underline`}
                  href={`mailto:info@wellnow.nz`}
                >
                  info@wellnow.nz
                </UnstyledLink>
                {` `}
                if this error persists.{` `}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
